/**
 * PathCapUsageSection — the "CAP" block of the preview hover card (MD-1147):
 * one line per capped offer — name + "used/limit" chip — for a path row
 * (every capped offer the bank links to that tracker path), or the single
 * offer for an offer row. Renders nothing when there is no cap to show,
 * nothing outside a QueryClientProvider (isolated hover-card specs), and
 * nothing for a tracker without bank offer caps (Tres Commas: its offer ids
 * equal Voluum's, so the Voluum caps would be read for it).
 * For Media Devoted | Created By James Alexandrov
 */

import { useContext, type FC } from 'react';
import { QueryClientContext } from '@tanstack/react-query';
import { capTimeframeNote } from '@trafficpulse/shared';

import { useOfferCapUsage, type OfferCapUsage } from '@/features/bank-stats';
import type { PreviewRow } from '@/features/flow-optimizer/components/preview/preview-row.types';
import { HoverSection } from '@/features/flow-optimizer/components/preview/hover/sections/hover-primitives';
import { capUsageFor } from '@/features/offer-caps/lib/cap-usage-for';
import {
  useFlowTracker,
  useDrawerProfile,
} from '@/features/flow-optimizer/tracker/flow-tracker-context';
import { cn } from '@/lib/utils';

const WARN_AT = 0.8;

const tone = (u: OfferCapUsage): string =>
  u.reached
    ? 'text-destructive'
    : u.used != null && u.used >= u.cap * WARN_AT
      ? 'text-warning'
      : 'text-foreground';

const windowLabel = (u: OfferCapUsage): string =>
  `${u.capType || 'cap'} ${capTimeframeNote(u.capTimeframe)}`;

/** The tracker offer ids a hover row stands for: the path's capped offers,
 *  or the offer itself. */
function offerIdsOf(row: PreviewRow, pathOffers: Record<string, string[]>): string[] {
  if (row.layer === 'offers') return [row.itemId];
  if (row.layer === 'paths') return pathOffers[row.itemId] ?? [];
  return [];
}

const Section: FC<{ row: PreviewRow }> = ({ row }) => {
  const tracker = useFlowTracker();
  const { data: response } = useOfferCapUsage();
  const data = capUsageFor(tracker, response);
  if (!data) return null;
  const usages = offerIdsOf(row, data.pathOffers)
    .map((id) => data.offers[id])
    .filter((u): u is OfferCapUsage => u != null);
  if (usages.length === 0) return null;
  return (
    <div className="border-b border-border/60 px-3 py-2.5 text-[11px]">
      <HoverSection label="CAP">
        {usages.map((u, i) => (
          <div key={`${u.offerName}-${i}`} className="flex items-baseline justify-between gap-3">
            <span className="min-w-0 truncate text-muted-foreground" title={u.offerName}>
              {u.offerName}
            </span>
            <span className="flex shrink-0 items-baseline gap-2 tabular-nums">
              <span className="text-[10px] text-muted-foreground">{windowLabel(u)}</span>
              <span className={cn('font-semibold', tone(u))}>
                {u.used == null ? '—' : u.used}/{u.cap}
              </span>
            </span>
          </div>
        ))}
      </HoverSection>
    </div>
  );
};

export const PathCapUsageSection: FC<{ row: PreviewRow }> = ({ row }) => {
  const client = useContext(QueryClientContext);
  const { offerCaps } = useDrawerProfile();
  return client && offerCaps ? <Section row={row} /> : null;
};

export default PathCapUsageSection;
